import { collection, doc, getDoc, getDocs, setDoc, deleteDoc } from 'firebase/firestore'
import { db } from '@/firebase'
import { RULE_EXPECTATIONS, attempt, accessDetail, fmtExpected } from '../ruleExpectations.js'
import { SMOKE_PRIVATE_STUDENT_ID_PREFIX } from '../taggedCollections.js'

// Contact PII split. validStudentPrivateFields is a closed key list — do not
// stamp __smokeTest (the write would fail the shape check). Both the orphan
// doc AND the smoke student use SMOKE-PRIV-* ids: studentPrivate shares the
// student's doc id, and a SPED chair can delete the student but NOT the
// private doc (super-admin only) — without the prefix that private doc would
// be a leftover no sweep could ever find.

function privatePayload(studentId, schoolId) {
  return {
    studentId,
    guardianContacts: [],
    ...(schoolId ? { schoolId } : {})
  }
}

export default {
  id: 'studentPrivate',
  label: 'Student private contacts',
  description:
    'studentPrivate list stays denied. A write with no parent student is denied (the rules get() the student). Parent-exists write is super-admin / staff-edit / related CM / psych — SPED chair is denied even on a student they created.',
  destructive: true,

  async run(ctx, step) {
    const role = ctx.roleClaim
    const schoolTier = Array.isArray(ctx.tokenSchoolIds)
    if (schoolTier && !ctx.schoolId) {
      step.block(
        'student private write',
        'Select a school before probing school-scoped studentPrivate'
      )
      return
    }

    const orphanId = `${SMOKE_PRIVATE_STUDENT_ID_PREFIX}${ctx.runId.slice(-8)}`
    const orphanRef = doc(db, 'studentPrivate', orphanId)
    const studentRef = doc(
      db,
      'students',
      `${SMOKE_PRIVATE_STUDENT_ID_PREFIX}STU-${ctx.runId.slice(-8)}`
    )
    const expectList = RULE_EXPECTATIONS.studentPrivateList()
    const expectOrphan = RULE_EXPECTATIONS.studentPrivateWrite(role, { parentExists: false })
    const expectOrphanRead = RULE_EXPECTATIONS.studentPrivateRead(role, { parentExists: false })
    const expectOrphanDelete = RULE_EXPECTATIONS.studentPrivateDelete(role, { parentExists: false })
    const expectOwnedDelete = RULE_EXPECTATIONS.studentPrivateDelete(role, {
      parentExists: true,
      parentInScope: true
    })
    const canCreateStudent = RULE_EXPECTATIONS.studentCreate(role)

    let studentCreated = false
    let orphanCreated = false
    let ownedCreated = false

    try {
      await step(`list studentPrivate (expected: ${fmtExpected(expectList)})`, async () => {
        const { allowed } = await attempt(() => getDocs(collection(db, 'studentPrivate')))
        return accessDetail(expectList, allowed)
      })

      await step(
        `write studentPrivate with no parent student (expected: ${fmtExpected(expectOrphan)})`,
        async () => {
          const { allowed } = await attempt(() =>
            setDoc(orphanRef, privatePayload(orphanId, ctx.schoolId))
          )
          orphanCreated = allowed
          return allowed
            ? `${accessDetail(expectOrphan, allowed)} — studentPrivate/${orphanId}`
            : accessDetail(expectOrphan, allowed)
        }
      )

      await step(
        `read orphan studentPrivate (expected: ${fmtExpected(expectOrphanRead)})`,
        async () => {
          const { allowed } = await attempt(() => getDoc(orphanRef))
          return accessDetail(expectOrphanRead, allowed)
        }
      )

      if (orphanCreated) {
        await step(
          `delete orphan studentPrivate (expected: ${fmtExpected(expectOrphanDelete)})`,
          async () => {
            const { allowed } = await attempt(() => deleteDoc(orphanRef))
            if (allowed) orphanCreated = false
            return accessDetail(expectOrphanDelete, allowed)
          }
        )
      } else {
        step.skip('delete orphan studentPrivate', 'Write denied — nothing to delete')
      }

      if (!canCreateStudent) {
        step.skip(
          'parent-exists studentPrivate',
          `${role} cannot create a student, so the parent-exists write path cannot be probed from this account.`
        )
        return
      }

      await step('create owned smoke student for private (expected: allowed)', async () => {
        const { allowed } = await attempt(() =>
          setDoc(studentRef, {
            app: {
              studentData: {
                firstName: 'SMOKE',
                lastName: `Priv ${ctx.runId.slice(-8)}`,
                plan: 'IEP',
                grade: '9',
                ssid: `SMOKE-PRIV-STU-${ctx.runId}`,
                caseManagerId: ctx.user.uid
              },
              accommodations: { instruction: '', assessment: '' },
              schedule: { classServices: [] },
              staffIds: []
            },
            __smokeTest: true,
            __smokeRunId: ctx.runId,
            ...(ctx.schoolId ? { schoolId: ctx.schoolId } : {})
          })
        )
        studentCreated = allowed
        return allowed
          ? `allowed, as expected — students/${studentRef.id}`
          : accessDetail(true, allowed)
      })

      if (!studentCreated) {
        step.skip('parent-exists studentPrivate write', 'Student was not created')
        return
      }

      const ownedRef = doc(db, 'studentPrivate', studentRef.id)
      const expectOwned = RULE_EXPECTATIONS.studentPrivateWrite(role, {
        parentExists: true,
        isRelated: true
      })
      const expectOwnedRead = RULE_EXPECTATIONS.studentPrivateRead(role, {
        parentExists: true,
        canReadStudent: true
      })

      await step(
        `write studentPrivate for a student you own (expected: ${fmtExpected(expectOwned)})`,
        async () => {
          const { allowed } = await attempt(() =>
            setDoc(ownedRef, privatePayload(studentRef.id, ctx.schoolId))
          )
          ownedCreated = allowed
          return allowed
            ? `${accessDetail(expectOwned, allowed)} — studentPrivate/${studentRef.id}`
            : accessDetail(expectOwned, allowed)
        }
      )

      await step(
        `read owned studentPrivate (expected: ${fmtExpected(expectOwnedRead)})`,
        async () => {
          const { allowed } = await attempt(() => getDoc(ownedRef))
          return accessDetail(expectOwnedRead, allowed)
        }
      )

      if (ownedCreated) {
        await step(
          `delete owned studentPrivate (expected: ${fmtExpected(expectOwnedDelete)})`,
          async () => {
            const { allowed } = await attempt(() => deleteDoc(ownedRef))
            if (allowed) ownedCreated = false
            return accessDetail(expectOwnedDelete, allowed)
          }
        )
      }
    } finally {
      await deleteDoc(orphanRef).catch(() => {})
      if (studentCreated) {
        await deleteDoc(doc(db, 'studentPrivate', studentRef.id)).catch(() => {})
        await deleteDoc(studentRef).catch(() => {})
      }
    }
  }
}
